import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@chotca/ui";
import { vi } from "@chotca/config";
import { WEB_APEX_URL } from "@/env";
import { Scribble } from "./scribble";

/**
 * Shared two-column accent-panel layout for all auth screens
 * (login / register / forgot-password / reset-password / verify-email / claim).
 *
 * Mirrors the apex web design — left panel is dark with brand copy aimed at
 * employers, right panel hosts the form. The apex/home link uses the marketing
 * site origin (WEB_APEX_URL) since the SPA itself has no home page.
 */
export function AuthLayout({
  label,
  title,
  subtitle,
  children,
  footer,
}: {
  label: string;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <main className="min-h-screen bg-paper text-ink">
      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-[1fr_1fr]">
        {/* LEFT — accent panel */}
        <aside className="relative hidden overflow-hidden bg-ink p-12 text-paper lg:flex lg:flex-col lg:justify-between">
          <a
            href={WEB_APEX_URL}
            aria-label={vi.common.appName}
            className="inline-flex w-fit"
          >
            <Logo variant="horizontal" size={28} onDark />
          </a>

          <div>
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-lime">
              {vi.landing.audienceEmployerLabel}
            </p>
            <h2 className="mt-5 font-satoshi text-[clamp(40px,5vw,64px)] font-black leading-[0.96] tracking-tight">
              {vi.landing.ctaBandTitle}
              <br />
              <Scribble tone="lime">
                {vi.landing.ctaBandTitleScribble}
              </Scribble>{" "}
              {vi.landing.ctaBandTitleEnd}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-paper/70">
              {vi.landing.employerSectionBody}
            </p>

            <div className="mt-10 flex flex-wrap gap-8">
              {vi.landing.heroStats.map((s) => (
                <div key={s.label} className="flex flex-col gap-1.5">
                  <span className="font-satoshi text-2xl font-black leading-none tracking-tight">
                    {s.num}
                  </span>
                  <span className="font-mono text-[10px] font-bold uppercase tracking-[0.1em] text-paper/55">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute -bottom-12 -right-12 -rotate-[6deg] rounded-3xl bg-lime px-7 py-6 text-ink shadow-[0_30px_60px_rgba(15,15,15,0.25)]">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-ink/60">
              {vi.landing.previewMatch}
            </div>
            <div className="mt-2 whitespace-pre-line font-satoshi text-2xl font-black leading-tight tracking-tight">
              {vi.landing.previewTitle}
            </div>
          </div>
        </aside>

        {/* RIGHT — form */}
        <section className="flex flex-col justify-center px-6 py-12 lg:px-16">
          <a
            href={WEB_APEX_URL}
            className="mb-10 inline-flex w-fit items-center gap-2 lg:hidden"
          >
            <Logo variant="square" size={32} />
            <span className="font-satoshi text-lg font-extrabold">
              {vi.common.appName}
            </span>
          </a>

          <div className="mx-auto w-full max-w-md">
            <p className="text-label font-bold uppercase tracking-[0.12em] text-ash">
              {label}
            </p>
            <h1 className="mt-3 font-satoshi text-4xl font-black leading-[0.96] tracking-tight md:text-5xl">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-3 text-base text-ink/70">{subtitle}</p>
            ) : null}

            <div className="mt-8">{children}</div>

            {footer ? (
              <div className="mt-8 border-t border-smoke pt-6 text-center text-sm">
                {footer}
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </main>
  );
}

export { Link as RouterLink };
